import { computed, ref, type Ref } from 'vue'
import {
  canStepDown,
  canStepUp,
  clampTokens,
  commitTokenDraft,
  draftRangeError,
  sanitizeTokenDraft,
} from '../utils/tokenQuantityUtils'

/**
 * The token count field. While editing, the typed digits live in `draft`
 * (kept even when out of range, with an inline error) and the parent only
 * ever receives a clamped value. Blur / Enter commits clamp(draft); an empty
 * field restores the previous value. Steppers and the slider rewrite the
 * draft when the field is being edited.
 */
export const useTokenDraft = (tokens: Ref<number>, min: Ref<number>, max: Ref<number>) => {
  /** Null when the field isn't being edited — it then shows `tokens`. */
  const draft = ref<string | null>(null)
  const committedBeforeEdit = ref(tokens.value)

  const fieldValue = computed(() => draft.value ?? String(tokens.value))
  const rangeError = computed(() =>
    draft.value === null ? null : draftRangeError(draft.value, min.value, max.value),
  )

  const onBeforeInput = (event: InputEvent) => {
    if (event.data && /[^0-9]/.test(event.data)) event.preventDefault()
  }

  const onInput = (event: Event) => {
    const input = event.target as HTMLInputElement
    if (draft.value === null) committedBeforeEdit.value = tokens.value
    const digits = sanitizeTokenDraft(input.value)
    draft.value = digits
    if (input.value !== digits) input.value = digits
    if (digits) tokens.value = clampTokens(Number(digits), min.value, max.value)
  }

  const onCommit = () => {
    if (draft.value === null) return
    tokens.value = commitTokenDraft(draft.value, committedBeforeEdit.value, min.value, max.value)
    draft.value = null
  }

  /** Slider / stepper moves — keep an open draft in step. */
  const setTokens = (next: number) => {
    tokens.value = clampTokens(next, min.value, max.value)
    if (draft.value !== null) draft.value = String(tokens.value)
  }

  return {
    fieldValue,
    rangeError,
    canDecrement: computed(() => canStepDown(tokens.value, min.value)),
    canIncrement: computed(() => canStepUp(tokens.value, max.value)),
    onBeforeInput,
    onInput,
    onCommit,
    setTokens,
  }
}
